import React, { useRef, useCallback, useEffect, useState } from 'react';
import { buttonClass, theme } from '../../../ui/theme';

export default function PlaylistPanel({
  isMinimized,
  showPlaylist,
  playlist,
  currentMusic,
  playlistPanelRef,
  onPlaylistChange,
  onRemoveFromPlaylist,
  getCurrentLanguageTitle,
  formatTime,
  t,
  i18n,
  spaNavigate,
  setPlaylist
}) {
  // 拖拽排序相关状态
  const [dragState, setDragState] = useState({ dragging: false, dragIndex: -1, overIndex: -1 });
  const dragItemRef = useRef(null);
  const dragItemHeightRef = useRef(0);
  const swipeStartRef = useRef(null);
  const mouseSwipeStartRef = useRef(null);
  const mouseSwipeMovedRef = useRef(false);
  const swipeRemoveTimerRef = useRef(null);
  const MIN_SWIPE_DISTANCE = 40; // 最小滑动距离（px），降低阈值使滑动更容易触发
  const [mouseSwipeActive, setMouseSwipeActive] = useState(false);

  const [swipeVisual, setSwipeVisual] = useState({
    index: -1,
    deltaX: 0,
    deleting: false,
    active: false
  });

  const handleRemove = useCallback((music) => {
    if (!music || !onRemoveFromPlaylist) return;
    onRemoveFromPlaylist(music);
  }, [onRemoveFromPlaylist]);

  const resetSwipeVisual = useCallback(() => {
    setSwipeVisual({ index: -1, deltaX: 0, deleting: false, active: false });
  }, []);

  // 处理触摸开始（用于向左滑动移除和拖拽排序）
  const handleTouchStart = useCallback((e, music, index) => {
    if (e.touches.length === 1) {
      swipeStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        music,
        index,
        time: Date.now()
      };
      setSwipeVisual({
        index,
        deltaX: 0,
        deleting: false,
        active: true
      });
      // 如果已经在拖拽状态，重置
      setDragState(prev => {
        if (prev.dragging) {
          return { dragging: false, dragIndex: -1, overIndex: -1 };
        }
        return prev;
      });
    }
  }, []);

  // 使用 ref 来存储最新的 dragState，避免闭包问题
  const dragStateRef = useRef(dragState);
  useEffect(() => {
    dragStateRef.current = dragState;
  }, [dragState]);

  // 处理触摸拖拽结束（提前定义，供 handleTouchEnd 使用）
  const handleTouchDragEnd = useCallback((e) => {
    setDragState(prev => {
      if (!prev.dragging || prev.dragIndex === -1) {
        swipeStartRef.current = null;
        return prev;
      }
      
      const fromIndex = prev.dragIndex;
      const toIndex = prev.overIndex;
      
      if (fromIndex !== toIndex && fromIndex >= 0 && toIndex >= 0 && setPlaylist) {
        const newPlaylist = [...playlist];
        const [moved] = newPlaylist.splice(fromIndex, 1);
        newPlaylist.splice(toIndex, 0, moved);
        setPlaylist(newPlaylist);
        
        // 如果移动的是当前播放的歌曲，更新当前音乐
        if (currentMusic && currentMusic.id === moved.id && onPlaylistChange) {
          onPlaylistChange(moved, toIndex);
        }
      }
      
      swipeStartRef.current = null;
      return { dragging: false, dragIndex: -1, overIndex: -1 };
    });
  }, [playlist, setPlaylist, currentMusic, onPlaylistChange]);

  // 处理触摸移动（用于向左滑动移除和启动拖拽排序）
  const handleTouchMove = useCallback((e) => {
    if (!swipeStartRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - swipeStartRef.current.x;
    const deltaY = touch.clientY - swipeStartRef.current.y;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    
    // 如果已经在拖拽状态，不处理（由 handleTouchDragMove 处理）
    if (dragStateRef.current.dragging) return;
    
    // 向左滑动（水平移动距离大于垂直移动距离，且向左移动）
    // 降低阈值，使滑动更容易触发
    if (absDeltaX > absDeltaY && deltaX < 0) {
      // 只在事件可取消时才调用 preventDefault
      if (e.cancelable) {
        e.preventDefault();
      }
      e.stopPropagation();
      setSwipeVisual(prev => ({
        ...prev,
        index: swipeStartRef.current.index,
        deltaX: Math.max(deltaX, -140),
        active: true
      }));
    }
    // 如果是垂直移动（用于拖拽排序），启动拖拽
    else if (absDeltaY > absDeltaX && absDeltaY > 10) {
      // 只在事件可取消时才调用 preventDefault
      if (e.cancelable) {
        e.preventDefault();
      }
      e.stopPropagation();
      const index = swipeStartRef.current.index;
      if (!dragItemHeightRef.current && e.currentTarget) {
        dragItemHeightRef.current = e.currentTarget.getBoundingClientRect().height || 0;
      }
      setDragState({ dragging: true, dragIndex: index, overIndex: index });
      dragItemRef.current = { index, music: playlist[index] };
      setSwipeVisual({ index: -1, deltaX: 0, deleting: false, active: false });
    }
  }, [handleRemove, playlist]);

  // 处理触摸结束
  const handleTouchEnd = useCallback((e) => {
    // 如果正在拖拽，完成拖拽排序
    if (dragStateRef.current.dragging && dragStateRef.current.dragIndex >= 0) {
      handleTouchDragEnd(e);
    } else {
      const activeIndex = swipeStartRef.current?.index ?? -1;
      const deltaX = swipeVisual.index === activeIndex ? swipeVisual.deltaX : 0;
      if (activeIndex >= 0 && deltaX <= -MIN_SWIPE_DISTANCE) {
        const musicToRemove = swipeStartRef.current?.music;
        setSwipeVisual(prev => ({
          ...prev,
          deleting: true,
          deltaX: -160
        }));
        if (swipeRemoveTimerRef.current) {
          clearTimeout(swipeRemoveTimerRef.current);
        }
        swipeRemoveTimerRef.current = setTimeout(() => {
          handleRemove(musicToRemove);
          setSwipeVisual({ index: -1, deltaX: 0, deleting: false, active: false });
        }, 180);
      } else {
        resetSwipeVisual();
      }
      swipeStartRef.current = null;
    }
  }, [handleTouchDragEnd, handleRemove, resetSwipeVisual, swipeVisual.deltaX, swipeVisual.index]);

  const handleMouseSwipeCancel = useCallback(() => {
    mouseSwipeStartRef.current = null;
    mouseSwipeMovedRef.current = false;
    setMouseSwipeActive(false);
    resetSwipeVisual();
  }, [resetSwipeVisual]);

  const handleMouseSwipeStart = useCallback((e, music, index) => {
    if (e.button !== 0) return;
    if (dragStateRef.current.dragging) return;
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
      return;
    }
    mouseSwipeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      music,
      index,
      time: Date.now()
    };
    mouseSwipeMovedRef.current = false;
    setSwipeVisual({
      index,
      deltaX: 0,
      deleting: false,
      active: true
    });
    setMouseSwipeActive(true);
  }, []);

  const handleMouseSwipeMove = useCallback((e) => {
    if (!mouseSwipeStartRef.current) return;
    if (dragStateRef.current.dragging) return;
    const deltaX = e.clientX - mouseSwipeStartRef.current.x;
    const deltaY = e.clientY - mouseSwipeStartRef.current.y;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    if (absDeltaX > absDeltaY && deltaX < 0) {
      mouseSwipeMovedRef.current = true;
      setSwipeVisual(prev => ({
        ...prev,
        index: mouseSwipeStartRef.current.index,
        deltaX: Math.max(deltaX, -140),
        active: true
      }));
    } else if (absDeltaY > absDeltaX && absDeltaY > 10) {
      mouseSwipeMovedRef.current = true;
      handleMouseSwipeCancel();
    }
  }, [handleMouseSwipeCancel]);

  const handleMouseSwipeEnd = useCallback((e) => {
    if (!mouseSwipeStartRef.current) return;
    const deltaX = e.clientX - mouseSwipeStartRef.current.x;
    const deltaY = e.clientY - mouseSwipeStartRef.current.y;
    const activeIndex = mouseSwipeStartRef.current.index ?? -1;
    const musicToRemove = mouseSwipeStartRef.current.music;
    const movedEnough = Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5;
    if (movedEnough) {
      mouseSwipeMovedRef.current = true;
    }

    if (activeIndex >= 0 && deltaX <= -MIN_SWIPE_DISTANCE && Math.abs(deltaX) > Math.abs(deltaY)) {
      setSwipeVisual(prev => ({
        ...prev,
        deleting: true,
        deltaX: -160
      }));
      if (swipeRemoveTimerRef.current) {
        clearTimeout(swipeRemoveTimerRef.current);
      }
      swipeRemoveTimerRef.current = setTimeout(() => {
        handleRemove(musicToRemove);
        resetSwipeVisual();
      }, 180);
    } else {
      resetSwipeVisual();
    }

    mouseSwipeStartRef.current = null;
    setMouseSwipeActive(false);
  }, [handleRemove, resetSwipeVisual]);

  // 处理拖拽开始（鼠标）
  const handleDragStart = useCallback((e, index) => {
    if (e.button !== 0) return; // 只处理左键
    e.preventDefault();
    e.stopPropagation();
    mouseSwipeStartRef.current = null;
    mouseSwipeMovedRef.current = false;
    setMouseSwipeActive(false);
    resetSwipeVisual();
    dragItemHeightRef.current = e.currentTarget?.getBoundingClientRect().height || 0;
    setDragState({ dragging: true, dragIndex: index, overIndex: index });
    dragItemRef.current = { index, music: playlist[index] };
  }, [playlist, resetSwipeVisual]);


  // 处理触摸拖拽移动
  const handleTouchDragMove = useCallback((e) => {
    setDragState(prev => {
      if (!prev.dragging || prev.dragIndex === -1 || e.touches.length !== 1) return prev;
      const touch = e.touches[0];
      const touchY = touch.clientY;
      
      // 找到当前触摸点对应的元素索引
      const container = playlistPanelRef?.current;
      if (!container) return prev;
      
      const items = container.querySelectorAll('[data-playlist-item]');
      let targetIndex = -1;
      items.forEach((item, idx) => {
        const rect = item.getBoundingClientRect();
        if (touchY >= rect.top && touchY <= rect.bottom) {
          targetIndex = idx;
        }
      });
      
      if (targetIndex >= 0 && targetIndex !== prev.overIndex) {
        // 只在事件可取消时才调用 preventDefault
        if (e.cancelable) {
          e.preventDefault();
        }
        e.stopPropagation();
        return { ...prev, overIndex: targetIndex };
      }
      return prev;
    });
  }, [playlistPanelRef]);

  // 使用原生事件监听器处理 touchmove（非 passive）
  useEffect(() => {
    const container = playlistPanelRef?.current;
    if (!container) return;

    const handleTouchMoveNative = (e) => {
      try {
        if (dragStateRef.current.dragging) {
          handleTouchDragMove(e);
        } else {
          handleTouchMove(e);
        }
      } catch (_) {}
    };

    container.addEventListener('touchmove', handleTouchMoveNative, { passive: false });

    return () => {
      container.removeEventListener('touchmove', handleTouchMoveNative);
    };
  }, [handleTouchMove, handleTouchDragMove, playlistPanelRef]);

  useEffect(() => {
    if (!mouseSwipeActive) return;
    const handleMove = (e) => handleMouseSwipeMove(e);
    const handleUp = (e) => handleMouseSwipeEnd(e);
    const handleBlur = () => handleMouseSwipeCancel();

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [handleMouseSwipeCancel, handleMouseSwipeEnd, handleMouseSwipeMove, mouseSwipeActive]);


  // 处理拖拽中
  const handleDragOver = useCallback((e, index) => {
    if (!dragState.dragging || dragState.dragIndex === -1) return;
    e.preventDefault();
    e.stopPropagation();
    if (dragState.overIndex !== index) {
      setDragState(prev => ({ ...prev, overIndex: index }));
    }
  }, [dragState.dragging, dragState.dragIndex, dragState.overIndex]);

  // 处理拖拽结束
  const handleDragEnd = useCallback((e) => {
    if (!dragState.dragging || dragState.dragIndex === -1) return;
    e.preventDefault();
    e.stopPropagation();
    
    const fromIndex = dragState.dragIndex;
    const toIndex = dragState.overIndex;
    
    if (fromIndex !== toIndex && fromIndex >= 0 && toIndex >= 0 && setPlaylist) {
      const newPlaylist = [...playlist];
      const [moved] = newPlaylist.splice(fromIndex, 1);
      newPlaylist.splice(toIndex, 0, moved);
      setPlaylist(newPlaylist);
      
      // 如果移动的是当前播放的歌曲，更新当前音乐
      if (currentMusic && currentMusic.id === moved.id && onPlaylistChange) {
        onPlaylistChange(moved, toIndex);
      }
    }
    
    setDragState({ dragging: false, dragIndex: -1, overIndex: -1 });
    dragItemRef.current = null;
  }, [dragState, playlist, setPlaylist, currentMusic, onPlaylistChange]);

  const getDragOffset = useCallback((index) => {
    if (!dragState.dragging || dragState.dragIndex === -1 || dragState.overIndex === -1) return 0;
    const height = dragItemHeightRef.current || 0;
    if (!height) return 0;
    if (index === dragState.dragIndex) return 0;
    if (dragState.dragIndex < dragState.overIndex) {
      if (index > dragState.dragIndex && index <= dragState.overIndex) {
        return -height;
      }
    } else if (dragState.dragIndex > dragState.overIndex) {
      if (index < dragState.dragIndex && index >= dragState.overIndex) {
        return height;
      }
    }
    return 0;
  }, [dragState.dragging, dragState.dragIndex, dragState.overIndex]);

  if (isMinimized) return null;
  if (!showPlaylist) return null;
  if (!playlist || playlist.length === 0) return null;
  if (!currentMusic) return null;

  return (
    <div
      ref={playlistPanelRef}
      className={['mt-4 max-h-48 overflow-y-auto overscroll-contain rounded p-2', theme.surfaces.modalPanel].join(' ')}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="space-y-0.5">
        {playlist.map((music, index) => (
          <div
            key={music.id}
            data-playlist-item
            draggable={true}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            onTouchStart={(e) => {
              // 如果触摸的是按钮，不处理滑动
              if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
                return;
              }
              handleTouchStart(e, music, index);
            }}
            onTouchEnd={handleTouchEnd}
            onMouseDown={(e) => {
              handleMouseSwipeStart(e, music, index);
            }}
            onClick={(e) => { 
              // 如果点击的是按钮，不处理
              if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
                return;
              }
              // 如果正在拖拽，不触发点击
              if (dragState.dragging) return;
              if (mouseSwipeMovedRef.current) {
                mouseSwipeMovedRef.current = false;
                return;
              }
              if (onPlaylistChange) onPlaylistChange(music, index); 
            }}
            className={`pl-2 pr-1 md:px-2 py-1 rounded cursor-pointer flex items-center gap-2 ${
              music.id === currentMusic.id
                ? 'bg-emerald-500/15 border border-emerald-400/30'
                : dragState.dragging && dragState.dragIndex === index
                ? 'opacity-70'
                : dragState.dragging && dragState.overIndex === index && dragState.dragIndex !== index
                ? 'bg-blue-500/20 border border-blue-400/30'
                : 'hover:bg-white/[0.04]'
            }`}
            style={{
              cursor: dragState.dragging && dragState.dragIndex === index ? 'grabbing' : (dragState.dragging ? 'grab' : 'pointer'),
              touchAction: 'pan-y',
              transform: `translate3d(${swipeVisual.index === index ? swipeVisual.deltaX : 0}px, ${getDragOffset(index)}px, 0) scale(${dragState.dragging && dragState.dragIndex === index ? 0.98 : 1})`,
              opacity: swipeVisual.index === index && swipeVisual.deleting ? 0 : 1,
              transition: dragState.dragging
                ? 'transform 160ms ease, opacity 160ms ease'
                : 'transform 200ms ease, opacity 200ms ease'
            }}
          >
            <span className="text-xs font-semibold text-white flex-1 min-w-0 truncate">
              {getCurrentLanguageTitle(music)}
            </span>
            {music.album && (
              <span className="text-xs text-white/55 flex-shrink-0">
                • {music.album}
              </span>
            )}
            {music.duration && (
              <span className="text-xs text-white flex-shrink-0 ml-auto">
                {formatTime(music.duration)}
              </span>
            )}
            <button
              type="button"
              className={buttonClass({ variant: 'outline', size: 'xs', className: 'ml-0.5 md:ml-2 flex-shrink-0' })}
              onClick={(e) => {
                try { e.stopPropagation(); } catch (_) {}
                const lang = i18n?.language || 'ja';
                if (music && music.id) spaNavigate(`/${lang}/music/${music.id}.html`);
              }}
              title={t('musicDetail.viewDetails', '查看详情')}
            >
              {t('musicDetail.viewDetails', '详情')}
            </button>
            {onRemoveFromPlaylist && (
              <button
                type="button"
                className={buttonClass({ variant: 'danger', size: 'xs', className: 'ml-0.5 md:ml-2 flex-shrink-0' })}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemove(music);
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                }}
                title={t('player.removeFromPlaylist', '移除')}
              >
                {t('player.removeFromPlaylist', '移除')}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

